import { ModuleContent, Section, Lesson, LessonPanel, Availability } from '../../Types'
import { SectionStatus, ModuleProfile } from '../../ModuleTypes'
import { moduleContent, simMod } from '../../Module.config'
import * as xAPI from '../../api/api'
import * as LMS from '../../LMS'
import { isDebug } from '../../App.config'
import { SessionType } from '../../contexts/ModuleContext'
import { isEmpty } from '../../Utils'


const content: ModuleContent = moduleContent

export const lrsCSS = 'padding: 2px 4px; color: #f47e56; font-size: 12px; border-left: 2px solid purple'
export const Sections: Section[] = content.sections

export const assetsPath = process.env.PUBLIC_URL + '/assets/learning-lab/'

export const section = (sectionID: number): Section => {
    if (!content.sections.length) throw new Error('Attempting to get a learning lab section when no sections are defined.')
    const section: Section | undefined = content.sections.find((section) => section.id === sectionID)
    if (!section) throw new Error(`No learning lab section with ID ${sectionID} `)
    return section
}

export const lesson = (sectionID: number, lessonID: number): Lesson => {
    //isDebug && console.log(`Getting lesson ID ${lessonID} of section ID ${sectionID}`)    
    const lesson = section(sectionID).lessons.find((lesson: Lesson) => lesson.id === lessonID)!
    validateLesson(lesson)
    return lesson
}

const validateLesson = (lesson: Lesson) => {
    if (lesson.faultID && !lesson.benchID) throw new Error(`Lesson '${lesson.LRSID}' is misconfigured! This lesson has a faultID, but is missing a benchID.`)
}

export const panel = (sectionID: number, lessonID: number, panelID: number): LessonPanel => {
    //isDebug && console.log(`Getting panel ID ${panelID} of lesson ID ${lessonID} of section ID ${sectionID}`)
    return lesson(sectionID, lessonID).panels.find((panel: any) => panel.id === panelID)!
}

export const allLessonsCompleted = (sectionID: number, userProfile: any): boolean => section(sectionID).lessons.every((lesson: Lesson) => {
    const lrsId = lrsID(sectionID, lesson.id)
    return isSectionStatus(userProfile, lrsId, SectionStatus.Completed)
})

export function lrsID(sectionID: number, lessonID: number | undefined): string {
    if (sectionID === 0) return ''
    return lessonID
        ? lesson(sectionID, lessonID).LRSID
        : section(sectionID).LRSID
}

export function isSectionStatus(userProfile: any, LRSID: string, status: SectionStatus): boolean | null {
    if (!(LRSID in userProfile) && simMod.isLab) return null
    return userProfile[LRSID] === status && simMod.isLab
}



export enum LRSState {
    Loading,
    Disabled,
    Ready,
    Completed
}

export const getLRSState = (userProfile: any, sectionID: number, lessonID: number | undefined): LRSState => {
    let LRSID = lrsID(sectionID, lessonID)
    if (LRSID === Availability.Disabled) return LRSState.Disabled

    let isChecked = isSectionStatus(userProfile, LRSID, SectionStatus.Completed)

    if (isChecked === null) return LRSState.Loading

    if (isChecked) return LRSState.Completed

    if (lessonID && lessonID > 1) {
        //If we're past the first lesson, check to see if the last lesson is complete
        const previousLessonID = lessonID - 1
        LRSID = lrsID(sectionID, previousLessonID)
        isChecked = isSectionStatus(userProfile, LRSID, SectionStatus.Completed)
        return isChecked ? LRSState.Ready : LRSState.Disabled
    }

    if (sectionID && sectionID > 1) {
        //If we're past the first section, check to see if the last section is complete
        const previousSectionID = sectionID - 1
        LRSID = lrsID(previousSectionID, lessonID)
        isChecked = isSectionStatus(userProfile, LRSID, SectionStatus.Completed)
        return isChecked ? LRSState.Ready : LRSState.Disabled
    }

    return LRSState.Ready
}

export const updateLRSStatus = async (context: any, lrsID: string, status: SectionStatus) => {
    const statusUpdate: Partial<ModuleProfile> = { [lrsID]: status }
    context.userProfile[lrsID] = status
    await xAPI.updateLRSProfile(context.user, statusUpdate)
}

export const setSectionAsStarted = async (sectionID: number, context: SessionType) => {
    const LRSID = lrsID(sectionID, undefined)
    const sectionNotStarted = isSectionStatus(context.userProfile, LRSID, SectionStatus.NotStarted)

    if (sectionNotStarted) {
        isDebug && console.log('%cSetting section as started...', lrsCSS)
        await updateLRSStatus(context, LRSID, SectionStatus.Started)
    }
}

export const setLessonAsStarted = async (sectionID: number, lessonID: number, context: SessionType) => {
    const LRSID = lrsID(sectionID, lessonID)
    const lessonNotStarted = isSectionStatus(context.userProfile, LRSID, SectionStatus.NotStarted)

    if (lessonNotStarted) {
        isDebug && console.log('%cSetting lesson as started...', lrsCSS)
        await updateLRSStatus(context, LRSID, SectionStatus.Started)
    }
}

export const setLessonAsCompleted = async (sectionId: number, lessonID: number, context: SessionType) => {
    const lessonLRSId = lrsID(sectionId, lessonID)
    const lessonIsStarted = isSectionStatus(context.userProfile, lessonLRSId, SectionStatus.Started)

    const sectionLRSID = lrsID(sectionId, undefined)
    const sectionIsStarted = isSectionStatus(context.userProfile, sectionLRSID, SectionStatus.Started)

    if (!lessonIsStarted && !sectionIsStarted) return // Don't do an update unless the current status is 'started'.

    const lessonUpdate: Partial<ModuleProfile> = lessonIsStarted ? { [lessonLRSId]: SectionStatus.Completed } : {}

    const allLessonsDone = allLessonsCompleted(sectionId, { ...context.userProfile, ...lessonUpdate })
    const sectionUpdate: Partial<ModuleProfile> = sectionIsStarted && allLessonsDone ? { [sectionLRSID]: SectionStatus.Completed } : {}

    const moduleScore = calculateScore({ ...context.userProfile, ...sectionUpdate, ...lessonUpdate })
    const scoreUpdate: Partial<ModuleProfile> = moduleScore !== context.userProfile['module-score'] ? { 'module-score': moduleScore } : {}
    const profileUpdate: Partial<ModuleProfile> = {
        ...sectionUpdate,
        ...lessonUpdate,
        ...scoreUpdate
    }
    if (isEmpty(profileUpdate)) return
    context.userProfile = { ...context.userProfile, ...profileUpdate }
    await xAPI.updateLRSProfile(context.user, profileUpdate)
        .then(() => context.LMSAvailable && LMS.setModuleScore(moduleScore))
}

export const handleLastPanel = async (sectionId: number, lessonId: number, context: SessionType) => {
    try {
        await setLessonAsCompleted(sectionId, lessonId, context)
            .then(() => {
                isDebug && console.log('%cLRS User Profile:', lrsCSS)
                isDebug && console.log(context.userProfile)
            })
    }
    catch (error) {
        console.error('Error handling last Panel', error)
    }
}

export const calculateScore = (userProfile: ModuleProfile): number => {
    if (!moduleContent.sections.length) throw new Error('Module content does not have any Sections.')

    isDebug && console.log('Recalculating score...')
    const availableLessons: Lesson[] = []
    moduleContent.sections.forEach(section => {
        if (section.LRSID === Availability.Disabled) return
        section.lessons.forEach(lesson => lesson.LRSID !== Availability.Disabled && availableLessons.push(lesson))
    })

    if (availableLessons.length === 0) throw new Error('There are no lessons available to calculate score.')

    let totalCompleted = 0
    availableLessons.forEach(lesson =>
        isSectionStatus(userProfile, lesson.LRSID, SectionStatus.Completed)
        && ++totalCompleted)
    const recalculatedScore = totalCompleted === 0 ? 0 : totalCompleted / availableLessons.length * 100

    isDebug && console.log('AVAILABLE LESSONS', availableLessons)
    isDebug && console.log('# OF COMPLETED LESSONS', totalCompleted)
    isDebug && console.log('RECALCULATED SCORE', recalculatedScore)
    return recalculatedScore
}